import { Button, Group, Modal, PasswordInput, Select, Stack, TextInput } from "@mantine/core";
import { useForm } from "@mantine/form";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { zodResolver } from "mantine-form-zod-resolver";
import type { ReactNode } from "react";
import { useEffect } from "react";
import { z } from "zod";

import { translateExternalSystem } from "../../../common/Dictionary";
import type {
  ExternalSystemSettingsResponseDto,
  StoreExternalSystemSettingsRequestDto,
  UpdateExternalSystemSettingsRequestDto,
} from "../../../generated/client/octalog-service";
import { ExternalSystem } from "../../../generated/client/octalog-service";
import {
  listExternalSystemsSettingsQueryKey,
  storeExternalSystemSettingsMutation,
  updateExternalSystemSettingsMutation,
} from "../../../generated/client/octalog-service/@tanstack/react-query.gen";
import { zExternalSystem } from "../../../generated/client/octalog-service/zod.gen";
import { useBaseMutation } from "../../../hooks/useBaseMutation";

interface ExternalSystemSettingsFormModalProps {
  /** Controls whether the modal is opened. */
  opened: boolean;
  /** Callback function to close the modal. */
  onClose: () => void;
  /** Initial data for editing an existing setting, or null for adding a new one. */
  initialData: ExternalSystemSettingsResponseDto | null;
}

const formSchema = z.object({
  system: zExternalSystem, // Use the generated Zod schema
  token: z.string().min(1, "Token darf nicht leer sein"),
});

type FormValues = z.infer<typeof formSchema>;

/**
 * Modal component for adding or editing external system settings.
 * Handles form validation and submission using appropriate mutations.
 */
export function ExternalSystemSettingsFormModal({
  opened,
  onClose,
  initialData,
}: ExternalSystemSettingsFormModalProps): ReactNode {
  const queryClient = useQueryClient();
  const isEditMode = initialData !== null;

  const { reset, setValues, ...form } = useForm<FormValues>({
    initialValues: {
      system: initialData?.system ?? ExternalSystem.LEXWARE, // Default to Lexware or existing
      token: "", // Always require re-entry for security
    },
    validate: zodResolver(formSchema),
  });

  useEffect(() => {
    if (opened) {
      reset();
      setValues({
        system: initialData?.system ?? ExternalSystem.LEXWARE,
        token: "", // Ensure token is cleared on open
      });
    }
  }, [initialData, opened, reset, setValues]);

  const storeMutationBaseOptions = useBaseMutation({
    ...storeExternalSystemSettingsMutation(),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: listExternalSystemsSettingsQueryKey() });
      onClose();
    },
    successMessage: "Externe System-Einstellung erfolgreich hinzugefügt.",
    errorMessage: "Fehler beim Hinzufügen der externen System-Einstellung.",
  });

  const updateMutationBaseOptions = useBaseMutation({
    ...updateExternalSystemSettingsMutation(),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: listExternalSystemsSettingsQueryKey() });
      onClose();
    },
    successMessage: "Externe System-Einstellung erfolgreich aktualisiert.",
    errorMessage: "Fehler beim Aktualisieren der externen System-Einstellung.",
  });

  const storeMutation = useMutation(storeMutationBaseOptions);
  const updateMutation = useMutation(updateMutationBaseOptions);

  const handleSubmit = (values: FormValues) => {
    if (isEditMode && initialData) {
      const updateDto: UpdateExternalSystemSettingsRequestDto = {
        token: values.token,
      };
      updateMutation.mutate({ path: { id: initialData.id }, body: updateDto });
    } else {
      const createDto: StoreExternalSystemSettingsRequestDto = {
        system: ExternalSystem[values.system as keyof typeof ExternalSystem],
        token: values.token,
      };
      storeMutation.mutate({ body: createDto });
    }
  };

  const isLoading = storeMutation.isPending || updateMutation.isPending;

  const systemOptions = Object.values(ExternalSystem).map((system) => ({
    value: system,
    label: translateExternalSystem(system),
  }));

  return (
    <Modal
      onClose={onClose}
      opened={opened}
      title={isEditMode ? "Einstellung bearbeiten" : "Neue Einstellung hinzufügen"}
    >
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Stack>
          {isEditMode && initialData ? (
            <TextInput label="System" readOnly value={translateExternalSystem(initialData.system)} />
          ) : (
            <Select
              data={systemOptions}
              label="System"
              placeholder="Externes System auswählen"
              required
              {...form.getInputProps("system")}
            />
          )}

          <PasswordInput
            autoComplete="new-password" // Prevent browser autofill
            data-1p-ignore // Prevent 1Password autofill
            description={isEditMode ? "Neues Token eingeben, um das vorhandene zu ändern." : undefined}
            label="API Token"
            placeholder="API Token eingeben"
            required
            {...form.getInputProps("token")}
          />

          <Group justify="flex-end" mt="md">
            <Button onClick={onClose} variant="default">
              Abbrechen
            </Button>
            <Button loading={isLoading} type="submit">
              {isEditMode ? "Speichern" : "Hinzufügen"}
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}
